import { useCallback } from "react"
import { ConnectorFieldMapping } from "../../../../../../types"

interface UseValueTransformsProps {
  fieldMapping: ConnectorFieldMapping
  isIncoming: boolean
  onChange: (mapping: ConnectorFieldMapping) => void
  getFieldNames: (
    key: string,
    value: any
  ) => { bbField: string; crmField: string }
}

export const useValueTransforms = ({
  fieldMapping,
  isIncoming,
  onChange,
  getFieldNames
}: UseValueTransformsProps) => {
  const getValueTransforms = useCallback((value: any): Record<string, any> => {
    if (typeof value === "object" && value !== null && "values" in value) {
      return value.values
    }
    return {}
  }, [])

  const updateValueTransform = useCallback(
    (key: string, sourceValue: string, targetValue: string) => {
      const newMapping = { ...fieldMapping }
      const currentValue = newMapping[key]
      const { bbField, crmField } = getFieldNames(key, currentValue)

      const values: any =
        typeof currentValue === "object" &&
        currentValue !== null &&
        "values" in currentValue
          ? { ...(currentValue as any).values }
          : {}

      if (targetValue) {
        // Special case for "default" - always store as "default": value
        if (sourceValue === "default") {
          values["default"] = targetValue
        } else if (isIncoming) {
          // For incoming: sourceValue is BB value, targetValue is CRM value
          // Store as: CRM value -> BB value
          // First, remove any existing entry that maps to this BB value
          Object.entries(values).forEach(([k, v]) => {
            if (v === sourceValue && k !== targetValue) {
              delete values[k]
            }
          })
          values[targetValue] = sourceValue
        } else {
          // For outgoing: sourceValue is BB value, targetValue is CRM value
          values[sourceValue] = targetValue
        }
      } else {
        // Removing
        if (isIncoming) {
          // Find and remove the entry where value === sourceValue
          Object.entries(values).forEach(([k, v]) => {
            if (v === sourceValue) delete values[k]
          })
        } else {
          delete values[sourceValue]
        }
      }

      if (isIncoming) {
        newMapping[key] =
          Object.keys(values).length > 0
            ? { key: bbField, values }
            : { key: bbField }
      } else {
        newMapping[key] =
          Object.keys(values).length > 0
            ? { key: crmField, values }
            : { key: crmField }
      }

      onChange(newMapping)
    },
    [fieldMapping, isIncoming, onChange, getFieldNames]
  )

  const removeValueTransform = useCallback(
    (key: string, sourceValue: string) => {
      const newMapping = { ...fieldMapping }
      const currentValue = newMapping[key]

      if (
        typeof currentValue === "object" &&
        currentValue !== null &&
        "values" in currentValue
      ) {
        const values = { ...(currentValue as any).values }
        delete values[sourceValue]

        const { bbField, crmField } = getFieldNames(key, currentValue)

        if (Object.keys(values).length === 0) {
          newMapping[key] = isIncoming ? { key: bbField } : { key: crmField }
        } else {
          newMapping[key] = { ...(currentValue as any), values }
        }
      }
      onChange(newMapping)
    },
    [fieldMapping, isIncoming, onChange, getFieldNames]
  )

  const removeAllValueTransforms = useCallback(
    (key: string) => {
      const newMapping = { ...fieldMapping }
      const currentValue = newMapping[key]
      const { bbField, crmField } = getFieldNames(key, currentValue)
      newMapping[key] = isIncoming ? { key: bbField } : { key: crmField }
      onChange(newMapping)
    },
    [fieldMapping, isIncoming, onChange, getFieldNames]
  )

  return {
    getValueTransforms,
    updateValueTransform,
    removeValueTransform,
    removeAllValueTransforms
  }
}
